import CalenderIcon from "@assets/images/calendar.svg";
import { InputBaseComponentProps } from "@material-ui/core";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import useDebounce from "@saleor/hooks/useDebounce";
import { makeStyles } from "@saleor/macaw-ui";
import locale from "date-fns/locale/en-US";
import moment from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";

import CustomActionBar from "./CustomActionBar";

interface StyleProps {
  placeholderColor?: string;
  allowEdit?: boolean;
}

export const useStyles = makeStyles<StyleProps>(
  theme => ({
    datePickerPopper: {
      "& .MuiPickersDay-root": {
        fontSize: "1.6rem",
        borderRadius: "10px"
      },
      "& .MuiPickersDay-root:focus": {
        backgroundColor: "#FFFFFF"
      },
      "& .MuiPickersDay-root.Mui-selected, .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus, .MuiPickersYear-yearButton.Mui-selected, .MuiPickersYear-yearButton.Mui-selected:hover, .MuiPickersYear-yearButton.Mui-selected:focus, .MuiPickersMonth-monthButton.Mui-selected, .MuiPickersMonth-monthButton.Mui-selected:hover, & .MuiPickersMonth-monthButton.Mui-selected:focus": {
        backgroundColor: "#009FB8"
      },
      "& .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus, .MuiPickersYear-yearButton.Mui-selected:hover, .MuiPickersMonth-monthButton.Mui-selected:hover": {
        transform: "scale(1.05)"
      },
      "& .MuiPickersDay-today:not(.Mui-selected)": {
        border: "1px solid #009FB8 !important"
      },
      "& .MuiPickersYear-yearButton, .MuiPickersMonth-monthButton": {
        fontSize: "1.5rem"
      },
      "& .MuiDayCalendar-weekDayLabel": {
        fontSize: "16px"
      },
      "& .MuiPickersCalendarHeader-label": {
        fontSize: "20px"
      },
      "& .MuiPickersCalendarHeader-switchViewIcon": {
        fontSize: "25px"
      },
      "& .MuiPickersArrowSwitcher-root": {
        "& .MuiIconButton-root": {
          fontSize: "2.5rem"
        }
      },
      "& .MuiPickersLayout-actionBar": {
        borderTop: " 1px solid #e0e0e0",
        marginRight: "25px",
        marginLeft: "25px",
        paddingTop: "15px",
        paddingBottom: "20px",
        "& .MuiButtonBase-root": {
          backgroundColor: "rgb(224 224 224)",
          width: "100%",
          color: "rgba(0, 0, 0, 0.87)"
        }
      },
      "& .MuiDayCalendar-slideTransition": {
        minHeight: "240px"
      }
    },
    datePickerv2: props => ({
      width: "100%",

      "& .MuiSvgIcon-root": {
        fontSize: "25px !important",
        marginLeft: "-26px"
      },
      "& .MuiOutlinedInput-input": {
        padding: "5px"
      },
      "& .noInput": {
        color: "#999999 !important"
      },
      "& .MuiInputBase-adornedEnd": {
        "& input": {
          pointerEvents: props?.allowEdit ? "initial" : "none",
          paddingLeft: "0.8rem",
          paddingRight: "0.8rem",
          "&::placeholder": {
            color: `${theme.palette.grey[500]} !important`
          }
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none"
        }
      }
    }),
    errorWithValue: {
      "& .MuiInputBase-root": {
        color: theme.palette.grey[900],
        backgroundColor: `${theme.palette.scgPrimary.hover} !important`,
        "& .Mui-disabled": {
          backgroundColor: `${theme.palette.scgPrimary.hover} !important`
        }
      }
    },
    placeHolder: {
      "&::placeholder": {
        color: `${theme.palette.grey[500]} !important`,
        fontSize: "1.2rem !important"
      }
    },
    disableCursor: {
      pointerEvents: "none"
    },
    clearButton: {
      border: "none",
      backgroundColor: "transparent",
      cursor: "pointer",
      position: "absolute",
      right: "33px",
      top: "6px",
      opacity: ".7",
      display: "inline-flex"
    },
    datePickerWrapper: {
      position: "relative"
    }
  }),
  { name: "DatePicker" }
);

export interface DatePickerProps {
  placement?: any;
  name: string;
  error?: boolean;
  value: string | Date;
  isReset?: boolean;
  onChange: (date) => void;
  onAccept?: (date) => void;
  defaultValuesProps?: string | Date;
  disableManualInput?: boolean;
  noBlock?: boolean;
  placeholderColor?: string;
  disabledInitialRendering?: boolean;
  helperText?: any;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: any;
  minDate?: string | number | Date | moment.Moment;
  maxDate?: string | number | Date | moment.Moment;
  disableFuture?: boolean;
  format?: string;
  dateRange?: number;
  type?: "gte" | "lte";
  isAllowTodayDate?: boolean;
  isUpdateDate?: boolean;
  id?: string;
  "data-testid"?: string;
  inputProps?: InputBaseComponentProps;
}

const DateFormat = "YYYY-MM-DD";
const formatValue = value => (value ? moment(value).format(DateFormat) : null);
if (locale && locale.options) {
  locale.options.weekStartsOn = 1;
}
const DatePickerComponent: React.FC<DatePickerProps> = ({
  "data-testid": dataTestId,
  name,
  error = false,
  value,
  isReset,
  onChange,
  helperText,
  onAccept,
  placeholder,
  disableManualInput = true,
  placeholderColor,
  noBlock,
  disabledInitialRendering,
  placement = "bottom",
  disableFuture,
  dateRange,
  type,
  isAllowTodayDate,
  id,
  inputProps,
  ...args
}) => {
  const classes = useStyles({ placeholderColor, allowEdit: false });

  const [date, setDate] = useState<Date | null>(value ? new Date(value) : null);
  const firstRender = React.useRef(true);
  const isChanged = useRef(false);

  useEffect(() => {
    if (!isChanged.current && !noBlock) {
      return;
    }
    setDate(value ? new Date(value) : null);
  }, [value]);

  useEffect(() => {
    if (args.isUpdateDate) {
      setDate(value ? new Date(value) : null);
    }
  }, [value]);

  const dateOnChange = useDebounce(setDate, 1000);

  const handleChange = (date: Date) => {
    isChanged.current = true;
    dateOnChange(date);
  };

  useEffect(() => {
    if (args.isUpdateDate) {
      setDate(value ? new Date(value) : null);
    }
  }, [value]);

  useEffect(() => {
    if (isReset) {
      if (!isChanged.current && !noBlock) {
        return;
      }
      setDate(null);
    }
  }, [isReset]);

  useEffect(() => {
    if (!isChanged.current && !noBlock) {
      return;
    }
    if (!disabledInitialRendering) {
      onChange({
        target: {
          name,
          value: formatValue(date)
        }
      });
      if (onAccept) {
        onAccept({
          target: {
            name,
            value: formatValue(date)
          }
        });
      }
    } else {
      if (!firstRender.current) {
        onChange({
          target: {
            name,
            value: formatValue(date)
          }
        });
        if (onAccept) {
          onAccept({
            target: {
              name,
              value: formatValue(date)
            }
          });
        }
      }
    }
  }, [date]);

  const minDate = args.minDate
    ? moment(args.minDate)
    : moment().subtract(10, "years");
  const maxDate = args.maxDate
    ? moment(args.maxDate)
    : moment().add(10, "years");

  const handleKeyDown = e => {
    setTimeout(() => {
      const inputElement = e.target as HTMLInputElement;
      inputElement.blur();
      e.target.setSelectionRange(0, e.target.value.length);
    }, 0);
  };

  const handleFocus = e => {
    setTimeout(() => {
      e.target.setSelectionRange(0, e.target.value.length);
    }, 0);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <div
        id={id}
        data-testid={dataTestId}
        data-input-type="datePicker-input-field"
        className={classes.datePickerWrapper}
      >
        <DesktopDatePicker
          views={["year", "month", "day"]}
          slots={{
            openPickerIcon: () => (
              <SVG src={CalenderIcon} width={18} height={18} />
            ),
            actionBar: props => (
              <CustomActionBar
                minDate={minDate}
                isAllowTodayDate
                maxDate={maxDate}
                dateRange={dateRange}
                type={type}
                {...props}
              />
            )
          }}
          slotProps={{
            actionBar: { actions: ["today"] },
            textField: {
              helperText,
              error,
              inputProps: {
                readOnly: true,
                onFocus: handleFocus,
                onKeyDown: e => {
                  e.preventDefault();
                  if (e.key === "Backspace" || e.key === "Delete") {
                    if (value) {
                      handleChange(null);
                    }
                    handleKeyDown(e);
                  }
                  return false;
                },
                className: date ? "" : "noInput",
                id: id ?? undefined,
                ...inputProps
              }
            },
            popper: {
              className: classes.datePickerPopper,
              placement
            }
          }}
          disabled={args.disabled}
          format="DD/MM/YYYY"
          value={moment(date)}
          defaultValue={moment(args.defaultValue)}
          onAccept={__date => {
            firstRender.current = false;
            handleChange(__date.toDate());
          }}
          minDate={minDate}
          maxDate={
            disableFuture
              ? moment(maxDate).isAfter(moment())
                ? moment()
                : maxDate
              : maxDate
          }
          className={classes.datePickerv2}
        />
      </div>
    </LocalizationProvider>
  );
};

export default React.memo(DatePickerComponent);
